import { useToast } from '@chakra-ui/react';
import { AccessLinkReadMetadata } from '@llx/common';
import { useEffect, useMemo, useState, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
  AccessLinkTypes,
  useIsValidAccessLinkQuery,
  useRequestReadAccessMutation,
} from '@/@generated/codegen/API';
import { useKeycloakAuth } from '@/auth/KeycloakProvider';
import {
  LoggedType,
  useUserAuthContext,
} from '@/components/AuthTokenContextProvider/UserAuthContext';
import GuestSignInForm from '@/features/meeting/GuestSignInForm';
import { ROUTES } from '@/routes/Routes';
import { PageLoading } from '@/ui/PageLoading';
import { getAccessLinkTargetUrl } from '@/utils/helpers/getAccessLinkUrl';
import { captureSentry } from '@/utils/helpers/sentryHelper';

/*
  Logic:
  STEP 1: Is the link valid ?
  - check if the accessLink is a valid one, if not, toast, redirect to home
  Now we have a validAccess link.

  STEP 2: Determining if we need to ask for a username
  - if we are not logged in: check if there is a forcedUsername attached to the link, if so then use that to create an account
  if not then ask for a username

  STEP 3: Determine if needs to switch account
  - if we are logged in: check if check if "linkFirstAccount" is set on the link, if so: then request a guest account, it will
  log you in to the linked account if not, do nothing

  STEP 4: Request access to the data
  - request access using the correct account


  OLD LOGIC: (keeping it for now)
  If I am not logged in:
  - then ask for a username
  - then I need a accessCode to create a temp account and get access to the masterClass.
  If this accessCode does not allow me to create an acc, then redirect to the home page for "wrong code" error.
  Now the user has an account, request access to the masterClass with the code
  If it works, then we're done

  If I am logged in:
  - if there is a accessCode, then request access to the masterClass
  if doesn't work, redirect home for "wrong code" error
  If it works, then we're done

  (Not handled edge case) -> If the user is logged in, and user has already access but uses a wrong code
  (Half handled edge case) -> If the user uses a correct code for another masterClass, we will let him in, but
  nothing will work since he doesn't actually have access to the masterClass so when trying to fetch stuff, it will fail
  (Hald handled edge case) -> If user has no accessCode and is logged in (not a guest) and does not have permission to the masterClass
  because he went to this url manually, then we will let him in but it will break because he has no access to the masterClass
*/
export function UseAccessLinkPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { accessCode } = useParams();
  if (!accessCode) {
    return <Navigate to="/" />;
  }
  const { askForGuestAccess, isRequestingGuestAccount } = useKeycloakAuth();
  const authContext = useUserAuthContext();
  const { status: userStatus } = authContext;
  const [shouldAskForUsername, setShouldAskForUsername] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const hasRequestedGuestAccess = useRef(false);
  const hasRequestedAccess = useRef(false);

  const {
    data: validAccessLinkQuery,
    loading: isValidAccessLinkLoading,
    refetch: refetchIsValidAccessLink,
  } = useIsValidAccessLinkQuery({
    variables: {
      code: accessCode,
    },
  });
  const validAccessLink = useMemo(() => {
    return validAccessLinkQuery?.isValidAccessLink ?? null;
  }, [validAccessLinkQuery]);
  const [mutationRequestAccess, { loading: isRequestingAccess }] = useRequestReadAccessMutation();

  const isLoading = isValidAccessLinkLoading || isRequestingAccess || isRequestingGuestAccount;

  const targetUrl = useMemo(() => {
    if (!validAccessLink) return null;
    return getAccessLinkTargetUrl({
      type: validAccessLink.purpose as AccessLinkTypes,
      objId: validAccessLink.objId,
      metadata: validAccessLink.metadata,
    });
  }, [validAccessLink]);

  const userId = useMemo(() => {
    if (authContext.status !== LoggedType.LOGGED_IN) return null;
    return authContext.userID;
  }, [authContext]);

  // NOTE: responsible for checking the code is valid
  useEffect(() => {
    if (!isValidAccessLinkLoading && !validAccessLink) {
      toast({
        status: 'error',
        title: 'Invalid link',
        description:
          'Maybe your link expired, an admininstrator disabled it or you are using the wrong link ?',
      });
      const timer = setTimeout(() => {
        navigate(ROUTES.ACCESS_LINK);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [validAccessLink, isValidAccessLinkLoading, navigate, toast]);

  // NOTE: responsible for asking the username if necessary
  useEffect(() => {
    if (!validAccessLink) return;

    // if the user already provided a username (from the GuestSignInForm), no need to ask
    if (username) {
      setShouldAskForUsername(false);
      return;
    }

    const metadata = validAccessLink.metadata as AccessLinkReadMetadata;

    // prefer forcedUsername when present
    if (metadata.forcedUsername) {
      setUsername(metadata.forcedUsername);
      setShouldAskForUsername(false);
      return;
    }

    if (metadata.linkedUserId) {
      // NOTE: it doesn't matter what username we will perform the request will, it will log us in with the linked account
      setUsername(metadata.linkedUserId);
      setShouldAskForUsername(false);
      return;
    }

    // if we are still loading user status, wait
    if (userStatus === LoggedType.LOADING) {
      setShouldAskForUsername(false);
      return;
    }

    // if the user is logged out, ask for a username (unless forcedUsername handled above)
    if (userStatus === LoggedType.LOGGED_OUT) {
      setShouldAskForUsername(true);
      return;
    }

    // user is logged in
    if (userStatus === LoggedType.LOGGED_IN) {
      // only ask for a username for logged-in users when the link requires creating/switching to a guest
      // (linkFirstAccount true and no account is linked yet)
      if (validAccessLink.linkFirstAccount && !metadata.linkedUserId) {
        setShouldAskForUsername(true);
      } else {
        setShouldAskForUsername(false);
      }
      return;
    }
  }, [userStatus, username, validAccessLink]);

  // NOTE: responsible for asking for a guest account using the username and accessCode
  // or switching account if already logged in
  useEffect(() => {
    if (!validAccessLink || isRequestingGuestAccount) return;
    const metadata = validAccessLink.metadata as AccessLinkReadMetadata;

    const shouldRequestGuest =
      (userStatus === LoggedType.LOGGED_OUT && !!username) ||
      (userStatus === LoggedType.LOGGED_IN &&
        validAccessLink.linkFirstAccount &&
        authContext.userID !== metadata.linkedUserId);

    if (!shouldRequestGuest) return;

    // if linkFirstAccount requires switching and there is no linked account yet, we must have username
    if (validAccessLink.linkFirstAccount && !metadata.linkedUserId && !username) {
      // wait for username input
      return;
    }

    // Prevent multiple calls
    if (hasRequestedGuestAccess.current) return;
    hasRequestedGuestAccess.current = true;

    askForGuestAccess({
      username: username || metadata.forcedUsername || 'guest-username',
      code: accessCode,
    })
      .then(() => {
        void refetchIsValidAccessLink();
      })
      .catch((err) => {
        console.log('use-access-link-page: failed to ask for guest access', err);
        captureSentry({
          title: 'Use access link page: askForGuestAccess failed',
          detail: {
            code: accessCode,
            userStatus,
          },
          error: err,
        });
        toast({
          status: 'error',
          title: 'Something went wrong',
          description: 'Unable to get access.',
        });
        setTimeout(() => {
          navigate(ROUTES.ACCESS_LINK, { replace: true });
        }, 3000);
      });
  }, [
    username,
    userStatus,
    validAccessLink,
    askForGuestAccess,
    isRequestingGuestAccount,
    accessCode,
    authContext.status,
    userId,
    navigate,
    toast,
  ]);

  // NOTE: if there is a accessCode, request access to the obj
  useEffect(() => {
    if (!validAccessLink || !targetUrl) return;

    // do not try to request access while a guest account request / switch is in progress
    if (isRequestingGuestAccount) return;

    if (userStatus !== LoggedType.LOGGED_IN) return;
    // NOTE: wait to be connected with the right account
    const metadata = validAccessLink.metadata as AccessLinkReadMetadata;
    if (validAccessLink.linkFirstAccount && authContext.userID !== metadata.linkedUserId) {
      return;
    }

    // Prevent multiple calls
    if (hasRequestedAccess.current) return;
    hasRequestedAccess.current = true;

    mutationRequestAccess({
      variables: {
        data: {
          code: accessCode,
        },
      },
    })
      .then(() => {
        console.log('use-access-link-page: access granted, redirecting');
        window.location.href = targetUrl;
      })
      .catch((err) => {
        console.error('use-access-link-page: failed to request access', err);
        captureSentry({
          title: 'use-access-link-page: request access failed',
          detail: {
            code: accessCode,
            userStatus,
            userId: authContext.userID,
          },
          error: err,
        });
        navigate(ROUTES.ACCESS_LINK, { replace: true });
      });
    // include isRequestingGuestAccount so effect re-runs after switching completes
  }, [
    userStatus,
    accessCode,
    validAccessLink,
    targetUrl,
    mutationRequestAccess,
    isRequestingGuestAccount,
    authContext.status,
    userId,
    navigate,
  ]);

  if (
    validAccessLink &&
    (validAccessLink.purpose as AccessLinkTypes) === AccessLinkTypes.ClientRegistration
  ) {
    return <Navigate to={`${ROUTES.REGISTER}/${accessCode}`} replace />;
  }

  if (isLoading) {
    return <PageLoading />;
  }

  if (shouldAskForUsername) {
    return (
      <GuestSignInForm
        isOpen={!username}
        onSubmit={(formData) => {
          setUsername(formData.username);
        }}
      />
    );
  }

  return <PageLoading />;
}
